import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { tap, withLatestFrom } from 'rxjs/operators';

import {
  HandoverTemplateActionTypes,
  HandoverTemplateAdd,
  HandoverTemplateApplyTemplate,
  HandoverTemplateApplyTemplateSuccess,
  HandoverTemplateChangeOrdinals,
  HandoverTemplateDelete,
  HandoverTemplateLoad,
  HandoverTemplateLoadSuccess,
  HandoverTemplateUpdate,
  HandoverTemplateUpdateSuccess,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import { HandoverTemplateService } from '../../services/handover-template.service';
import { ApplyTemplateResult, HandoverTemplate, Project } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class HandoverTemplateEffect {
  constructor(
    private actions$: Actions,
    private handoverTemplateService: HandoverTemplateService,
    private store: Store<State>,
    private toastr: NotificationService,
  ) {}

  loadHandoverTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverTemplateLoad>(HandoverTemplateActionTypes.HandoverTemplateLoad),
      withLatestFrom(this.store.select(getCurrentProject)),
      trackResource<HandoverTemplate>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverTemplateService,
        method: 'getHandoverTemplate',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new HandoverTemplateLoadSuccess(response),
      }),
    ),
  );

  changeTemplateOrdinals$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverTemplateChangeOrdinals>(HandoverTemplateActionTypes.HandoverTemplateChangeOrdinals),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HandoverTemplate>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverTemplateService,
        method: 'updateHandoverTemplateOrdinals',
        serviceArgs: ([action, currentProject]) => [
          currentProject.id,
          action.sections.map((section, index) => ({
            id: section.id,
            ordinal: index + action.startIndex + 1,
            projectId: section.projectId,
          })),
        ],
        onSuccess: (response) => new HandoverTemplateUpdateSuccess(response),
      }),
    ),
  );

  deleteHandoverTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverTemplateDelete>(HandoverTemplateActionTypes.HandoverTemplateDelete),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HandoverTemplate>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverTemplateService,
        method: 'deleteHandoverTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.handoverTemplate.id],
        onSuccess: (response) => new HandoverTemplateUpdateSuccess(response),
      }),
    ),
  );

  addHandoverTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverTemplateAdd>(HandoverTemplateActionTypes.HandoverTemplateAdd),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HandoverTemplate>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverTemplateService,
        method: 'addHandoverTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.handoverTemplate],
        onSuccess: (response) => new HandoverTemplateUpdateSuccess(response),
      }),
    ),
  );

  updateHandoverTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverTemplateUpdate>(HandoverTemplateActionTypes.HandoverTemplateUpdate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<HandoverTemplate>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverTemplateService,
        method: 'updateHandoverTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.handoverTemplates],
        onSuccess: (response) => new HandoverTemplateUpdateSuccess(response),
      }),
    ),
  );

  applyTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverTemplateApplyTemplate>(HandoverTemplateActionTypes.HandoverTemplateApplyTemplate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource<ApplyTemplateResult>({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.handoverTemplateService,
        method: 'applyTemplate',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (result) => new HandoverTemplateApplyTemplateSuccess(result.updatedCount),
      }),
    ),
  );

  applyTemplateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<HandoverTemplateApplyTemplateSuccess>(HandoverTemplateActionTypes.HandoverTemplateApplyTemplateSuccess),
        tap(({ updatedCount }) =>
          this.toastr.success(`Template applied successfully. ${updatedCount} unsigned handover${updatedCount !== 1 ? 's' : ''} updated.`),
        ),
      ),
    { dispatch: false },
  );
}
